import React, { useState } from "react";

import {
    Button,
    Checkbox,
    LabeledField,
    LanguageSegmentedControl,
    SectionCard,
    Spinner,
    Stack,
    TextInput,
    Textarea,
    ToggleSwitch,
    UploadDropzone,
} from "shared/ui";
import {
    IMAGE_UPLOAD_ACCEPT,
    INFO_MESSAGE_IMAGE_MAX_BYTES,
    imageUploadHint,
    validateImageFile,
} from "shared/lib/mediaUpload";
import { uploadInfoMessageImage } from "services/uploads/rest";
import { TariffVariant } from "modules/settings/models";

import {
    LOCALE_LABELS,
    PRIMARY_LOCALE,
    SELECTABLE_LOCALES,
    STAFF_CALL_TYPES,
} from "features/MenuSettings/constants";
import { useMenuSettingsContext } from "features/MenuSettings/context";
import { MenuSettingsFlags } from "features/MenuSettings/types";

import { MenuLinkCard } from "./components/MenuLinkCard";
import { InfoImage, InfoImageRow, Languages, Toggles } from "./styles";

interface FlagMeta {
    flag: keyof MenuSettingsFlags;
    label: string;
    hint: string;
    tariff: TariffVariant;
    regularOnly?: boolean;
}

const FLAGS: FlagMeta[] = [
    {
        flag: "isMenuEnabled",
        label: "Включить меню",
        hint: "Красивое меню с фотографиями и описанием",
        tariff: "SimpleV2",
    },
    {
        flag: "isCartEnabled",
        label: "Включить корзину",
        hint: "Позволяет гостям добавлять блюда в корзину",
        tariff: "Basic",
    },
    {
        flag: "isOrderCreationEnabled",
        label: "Получать заказы из меню",
        hint: "Увеличивает выручку и сокращает расходы",
        tariff: "Basic",
    },
    {
        flag: "isPromoCodesEnabled",
        label: "Включить скидки и промокоды",
        hint: "Создавайте промокоды в % или фиксированные, дарите сертификаты.",
        tariff: "Basic",
    },
    {
        flag: "isLoyaltyProgramEnabled",
        label: "Включить бонусную программу",
        hint: "Начисляйте и списывайте бонусные баллы по номеру телефона",
        tariff: "Basic",
        regularOnly: true,
    },
];

export const SettingsTab = () => {
    const { menuFlags, menuType, ensureTariff, locale, notify } = useMenuSettingsContext();
    const [activeLang, setActiveLang] = useState<string>(locale);
    const [uploadingImage, setUploadingImage] = useState<boolean>(false);

    const uploadImage = async (files: File[]) => {
        if (files.length === 0) return;

        const error = validateImageFile(files[0], INFO_MESSAGE_IMAGE_MAX_BYTES);

        if (error !== null) {
            notify.showError(error);
            return;
        }

        setUploadingImage(true);
        try {
            const result = await uploadInfoMessageImage(files[0]);
            menuFlags.setInfoMessageMediaUrl(result.url);
        } catch (e) {
            notify.showError("Не удалось загрузить изображение", e);
        } finally {
            setUploadingImage(false);
        }
    };

    const visibleFlags = FLAGS.filter(
        (item) => !item.regularOnly || menuType === "Regular"
    );

    return (
        <>
            <SectionCard title="Возможности меню">
                <Toggles>
                    {visibleFlags.map((item) => (
                        <ToggleSwitch
                            key={item.flag}
                            checked={menuFlags.flags[item.flag]}
                            label={item.label}
                            hint={item.hint}
                            onChange={(checked) => {
                                if (!ensureTariff(item.tariff)) return;
                                menuFlags.setFlag(item.flag, checked);
                            }}
                        />
                    ))}
                </Toggles>
            </SectionCard>

            {menuType === "Regular" && (
                <SectionCard
                    title="Вызов персонала"
                    description="Гость вызовет сотрудника кнопкой в меню. Вызов уходит в чаты, отмеченные на вкладке «Уведомления»."
                >
                    <Toggles>
                        {STAFF_CALL_TYPES.map((item) => (
                            <ToggleSwitch
                                key={item.kind}
                                checked={menuFlags.staffCalls.indexOf(item.kind) >= 0}
                                label={item.title}
                                hint={item.description}
                                onChange={(checked) => {
                                    if (!ensureTariff("SimpleV2")) return;
                                    menuFlags.toggleStaffCall(item.kind, checked);
                                }}
                            />
                        ))}
                    </Toggles>
                </SectionCard>
            )}

            <SectionCard
                title="Языки перевода"
                description="Гость сможет переключить меню на выбранные языки."
            >
                <Languages>
                    {SELECTABLE_LOCALES.map((item) => (
                        <Checkbox
                            key={item}
                            checked={menuFlags.enabledLocales.indexOf(item) >= 0}
                            disabled={item === PRIMARY_LOCALE}
                            label={LOCALE_LABELS[item]}
                            onChange={(checked) => {
                                if (!ensureTariff("SimpleV2")) return;
                                menuFlags.toggleLocale(item, checked);
                            }}
                        />
                    ))}
                </Languages>
            </SectionCard>

            <SectionCard
                title="Информационный поп-ап"
                description="Сообщение, которое гость увидит при открытии меню."
                headerRight={
                    menuFlags.infoMessage.isEnabled ? (
                        <LanguageSegmentedControl
                            languages={menuFlags.enabledLocales}
                            activeLang={activeLang}
                            primaryLang={locale}
                            onChange={setActiveLang}
                            isMissing={(lang) => !menuFlags.infoMessage.title[lang]}
                        />
                    ) : undefined
                }
            >
                <Stack>
                    <ToggleSwitch
                        checked={menuFlags.infoMessage.isEnabled}
                        label="Показывать поп-ап"
                        onChange={(checked) => {
                            if (!ensureTariff("SimpleV2")) return;
                            menuFlags.setInfoMessageEnabled(checked);
                        }}
                    />

                    {menuFlags.infoMessage.isEnabled && (
                        <>
                            <LabeledField label="Заголовок">
                                <TextInput
                                    value={menuFlags.infoMessage.title[activeLang]}
                                    onChange={(value) =>
                                        menuFlags.setLocalizedInfoMessageField(
                                            "title",
                                            value,
                                            activeLang
                                        )
                                    }
                                />
                            </LabeledField>

                            <LabeledField label="Описание">
                                <Textarea
                                    value={menuFlags.infoMessage.description[activeLang]}
                                    rows={3}
                                    onChange={(value) =>
                                        menuFlags.setLocalizedInfoMessageField(
                                            "description",
                                            value,
                                            activeLang
                                        )
                                    }
                                />
                            </LabeledField>

                            <LabeledField label="Текст кнопки">
                                <TextInput
                                    value={menuFlags.infoMessage.callToAction[activeLang]}
                                    onChange={(value) =>
                                        menuFlags.setLocalizedInfoMessageField(
                                            "callToAction",
                                            value,
                                            activeLang
                                        )
                                    }
                                />
                            </LabeledField>

                            <LabeledField label="Изображение">
                                {uploadingImage ? (
                                    <Spinner color="secondary" />
                                ) : menuFlags.infoMessage.mediaUrl ? (
                                    <InfoImageRow>
                                        <InfoImage
                                            src={menuFlags.infoMessage.mediaUrl}
                                            alt=""
                                        />
                                        <Button
                                            variant="text"
                                            color="danger"
                                            onClick={() =>
                                                menuFlags.setInfoMessageMediaUrl("")
                                            }
                                        >
                                            Удалить
                                        </Button>
                                    </InfoImageRow>
                                ) : (
                                    <UploadDropzone
                                        onDrop={uploadImage}
                                        width={240}
                                        height={140}
                                        accept={IMAGE_UPLOAD_ACCEPT}
                                        hint={imageUploadHint(INFO_MESSAGE_IMAGE_MAX_BYTES)}
                                    />
                                )}
                            </LabeledField>
                        </>
                    )}
                </Stack>
            </SectionCard>

            <MenuLinkCard />
        </>
    );
};
